import { type ReactNode, useEffect } from 'react';

interface ModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  footer?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}

export function Modal({ title, subtitle, onClose, footer, wide = false, children }: ModalProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="fixed inset-0 h-full w-full cursor-default bg-slate-900/40"
      />
      <div className="relative flex min-h-full items-start justify-center p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className={`mt-10 w-full rounded-lg bg-white shadow-xl ${wide ? 'max-w-5xl' : 'max-w-2xl'}`}
        >
          <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
            <div>
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
              {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-blue-600 hover:underline"
            >
              Close
            </button>
          </header>
          <div className="px-5 py-4">{children}</div>
          {footer && (
            <footer className="flex items-center gap-3 border-t border-gray-200 px-5 py-3">
              {footer}
            </footer>
          )}
        </div>
      </div>
    </div>
  );
}
